/**
 * 学习乐园 · 比大小（game-bijiao.js）
 * ============================================================
 * 「排序与序列」组第三作：分数、小数、百分数（挑战档含负数）混排出题，
 * 玩法是「每轮点出剩下最小的那个」——比较排序的人类版。与排排序的
 * 「点两下交换」互补：那边考「已知的顺序感」，这边考「两两比较推出全序」，
 * 正好补上知识连连看（认分数）与 24 点（算分数）之间的「比较」一环。
 *
 * 反馈底线（栏目同款）：点错不惩罚，说「它不是现在最小的」，想不出点
 * 「查一查」——当场拿最小和次小比给你看：通分、化小数、逐位比，讲的是
 * 真过程，不是「答案是 X」。查的次数如实计入。
 *
 * 出题底线：一局内两两不等（相邻差 ≥ 0.01，入门档 ≥ 0.2）——比较题出
 * 平局是伪题；生成器整局重试保证。
 *
 * 口径：三档 3 张（默认，铁律：默认档必须最简单）/ 5 张 / 5 张含负数；
 * 计时（比较是速算成分为主的品类，用时是合理成绩维度）；只存本机。
 *
 * 冒烟钩子：window.STBijiao 暴露纯引擎（levelSpec / gen / cmp / why / fmt）。
 */
(function (global) {
  'use strict';

  var PREF_KEY = 'study-bijiao';
  var RECORD_KEY = 'study-bijiao-record';

  var LEVELS = {
    1: { label: '入门 3 张', hint: '默认档 · 整数与小数', n: 3, gap: 0.2 },
    2: { label: '进阶 5 张', hint: '分数小数百分数', n: 5, gap: 0.01 },
    3: { label: '挑战 5 张', hint: '带负数 · 更刁的分母', n: 5, gap: 0.01 },
  };

  /* ============ 小工具 ============ */

  function gcd(a, b) { return b ? gcd(b, a % b) : a; }
  function lcm(a, b) { return a * b / gcd(a, b); }
  function rint(a, b) { return a + Math.floor(Math.random() * (b - a + 1)); }
  function pick(arr) { return arr[Math.floor(Math.random() * arr.length)]; }

  /* ============ 纯引擎（无 DOM） ============ */

  function levelSpec(level) {
    var lv = LEVELS[level] ? level : 1;
    return { level: lv, n: LEVELS[lv].n, gap: LEVELS[lv].gap };
  }

  /* —— 数的四种牌面：int / dec / frac / pct —— text 是牌面原文，value 是统一量 —— */

  function genInt(neg) {
    var v = rint(1, 99);
    return neg ? { kind: 'int', text: '-' + v, value: -v } : { kind: 'int', text: String(v), value: v };
  }
  function genDec(neg) {
    var places = pick([1, 2, 3]);
    var v = rint(1, Math.pow(10, places) - 1);
    var text = (v / Math.pow(10, places)).toFixed(places);
    return neg
      ? { kind: 'dec', text: '-' + text, value: -v / Math.pow(10, places) }
      : { kind: 'dec', text: text, value: v / Math.pow(10, places) };
  }
  function genFrac(hard, neg) {
    var den = hard ? pick([6, 7, 9, 11, 12]) : pick([2, 3, 4, 5, 6, 8, 10]);
    var num = Math.random() < 0.3 && hard ? rint(den + 1, den * 3) : rint(1, den - 1);   // 偶尔出假分数（挑战档）
    return neg
      ? { kind: 'frac', text: '-' + num + '/' + den, value: -num / den }
      : { kind: 'frac', text: num + '/' + den, value: num / den };
  }
  function genPct() {
    var v = Math.random() < 0.4 ? rint(5, 399) / 10 : rint(5, 200);   // 37.5% 或整百分数
    var text = (v % 1 ? v.toFixed(1) : String(v)) + '%';
    return { kind: 'pct', text: text, value: v / 100 };
  }

  /**
   * 出一局：n 张、两两不等（相邻差 ≥ gap）、牌面原文不重复。
   * 不满足整局重试（上限 200 次，结构性必成功：值域远大于 gap 粒度）。
   */
  function gen(level) {
    var spec = levelSpec(level);
    var n = spec.n, gap = spec.gap;
    for (var t = 0; t < 200; t++) {
      var plan = planKinds(level, n);
      var nums = plan.kinds.map(function (k, i) { return genOne(k, level, plan.negIdxs.indexOf(i) >= 0); });
      var vals = nums.map(function (x) { return x.value; }).sort(function (a, b) { return a - b; });
      var okGap = true;
      for (var i = 1; i < vals.length; i++) {
        if (vals[i] - vals[i - 1] < gap) { okGap = false; break; }
      }
      var texts = {};
      var okText = nums.every(function (x) { return !texts[x.text] && (texts[x.text] = true); });
      if (okGap && okText) return { level: spec.level, n: n, nums: nums };
    }
    /* 兜底（理论不可达）：固定一套入门题 */
    return { level: spec.level, n: n, nums: n === 3
      ? [{ kind: 'int', text: '12', value: 12 }, { kind: 'dec', text: '8.5', value: 8.5 }, { kind: 'int', text: '3', value: 3 }]
      : [{ kind: 'frac', text: '1/2', value: 0.5 }, { kind: 'dec', text: '0.25', value: 0.25 }, { kind: 'pct', text: '75%', value: 0.75 },
         { kind: 'int', text: '2', value: 2 }, { kind: 'dec', text: '1.5', value: 1.5 }] };
  }

  function planKinds(level, n) {
    if (level === 1) return { kinds: ['int', 'dec', 'dec'], negIdxs: [] };
    if (level === 2) {
      var base = ['frac', 'dec', 'pct'];
      while (base.length < n) base.push(pick(['int', 'dec', 'frac', 'pct']));
      return { kinds: base, negIdxs: [] };
    }
    // 挑战档：最后一张强制负数——「带负数」是档位承诺，不靠运气
    var hard = ['frac', 'dec', 'pct', pick(['frac', 'dec'])];
    hard.push(pick(['int', 'dec', 'frac']));
    return { kinds: hard, negIdxs: [hard.length - 1] };
  }

  function genOne(kind, level, forceNeg) {
    var neg = level === 3 && kind !== 'pct' && (forceNeg || Math.random() < 0.5);
    if (kind === 'int') return genInt(neg);
    if (kind === 'dec') return genDec(neg);
    if (kind === 'frac') return genFrac(level === 3, neg);
    return genPct();
  }

  function cmp(a, b) {
    return a.value < b.value ? -1 : a.value > b.value ? 1 : 0;
  }

  function fmt(v) { return v.text; }

  /**
   * 比较讲解（栏目底线：讲真过程）——返回「a 与 b 谁小谁大 + 为什么」的文案。
   * 统一量是 value，但讲解按牌面类型走人话：分数通分、百分数化小数、
   * 小数逐位比、负数讲绝对值。
   */
  function why(a, b) {
    var big = a.value > b.value ? a : b;
    var small = a.value > b.value ? b : a;
    var head = '「' + a.text + '」和「' + b.text + '」比：';
    var body;
    var an = a.value < 0, bn = b.value < 0;
    if (an !== bn) {
      body = (an ? '「' + a.text + '」是负数，负数都小于 0；「' + b.text + '」是正数，大于 0。'
                 : '「' + b.text + '」是负数，负数都小于 0；「' + a.text + '」是正数，大于 0。');
    } else if (an && bn) {
      body = '两个都是负数，绝对值大的反而小：|' + small.text + '| = ' + Math.abs(small.value).toFixed(4).replace(/\.?0+$/, '')
        + '，|' + big.text + '| = ' + Math.abs(big.value).toFixed(4).replace(/\.?0+$/, '')
        + '，所以 ' + small.text + ' 更小。';
    } else if (a.kind === 'frac' && b.kind === 'frac') {
      body = fracFrac(a, b, small, big);
    } else if (a.kind === 'dec' && b.kind === 'dec') {
      body = decDec(a, b, small, big);
    } else {
      body = mixed(a, b, small, big);
    }
    return head + body + '（所以先点「' + small.text + '」）';
  }

  function fracFrac(a, b, small, big) {
    var d1 = Number(a.text.split('/')[1]), d2 = Number(b.text.split('/')[1]);
    var n1 = Number(a.text.replace('-', '').split('/')[0]), n2 = Number(b.text.replace('-', '').split('/')[0]);
    var m = lcm(d1, d2);
    var n1s = n1 * m / d1, n2s = n2 * m / d2;
    return '通分：分母 ' + d1 + ' 和 ' + d2 + ' 的最小公倍数是 ' + m + '——'
      + a.text + ' = ' + n1s + '/' + m + '，' + b.text + ' = ' + n2s + '/' + m + '，'
      + n1s + ' ' + (n1s < n2s ? '<' : '>') + ' ' + n2s + '，所以 ' + small.text + ' 更小。';
  }

  function decDec(a, b, small, big) {
    var x = a.value, y = b.value;
    var ia = Math.floor(Math.abs(x)), ib = Math.floor(Math.abs(y));
    if (ia !== ib) {
      return '先比整数部分：' + ia + ' ' + (ia < ib ? '<' : '>') + ' ' + ib + '，所以 ' + small.text + ' 更小。';
    }
    var sa = a.text.replace('-', ''), sb = b.text.replace('-', '');
    var len = Math.max(sa.length, sb.length);
    for (var i = 0; i < len; i++) {
      var ca = sa[i] == null ? '0' : sa[i], cb = sb[i] == null ? '0' : sb[i];
      if (ca !== cb && ca !== '.' && cb !== '.') {
        // 字符索引 1 是小数点：十分位从索引 2 起
        var posName = i === 0 ? '个位' : i === 2 ? '十分位' : i === 3 ? '百分位' : i === 4 ? '千分位' : '更高位';
        return '整数部分相同，' + posName + '上 ' + ca + ' ' + (Number(ca) < Number(cb) ? '<' : '>') + ' ' + cb + '，所以 ' + small.text + ' 更小。';
      }
    }
    return '按小数逐位比，' + small.text + ' 更小。';
  }

  function mixed(a, b, small, big) {
    var f = a.kind === 'frac' ? a : (b.kind === 'frac' ? b : null);
    var p = a.kind === 'pct' ? a : (b.kind === 'pct' ? b : null);
    var parts = [];
    if (p) {
      parts.push('「' + p.text + '」就是 ' + p.text.replace('%', '') + '÷100 = ' + trim(p.value));
    }
    if (f) {
      var fr = f.text.split('/');
      parts.push('「' + f.text + '」化成小数：' + fr[0] + '÷' + fr[1] + ' = ' + trim(f.value));
    }
    var rest = [a, b].filter(function (x) { return x !== f && x !== p; })[0];
    if (rest) parts.push('「' + rest.text + '」就是 ' + trim(rest.value));
    parts.push(trim(small.value) + ' < ' + trim(big.value) + '，所以 ' + small.text + ' 更小');
    return parts.join('；') + '。';
  }

  function trim(v) {
    var s = v.toFixed(4);
    return s.replace(/0+$/, '').replace(/\.$/, '');
  }

  /* ============ UI：状态与工具 ============ */

  var root = null;
  var ui = {};
  var S = {
    level: 1, nums: [], pool: [], slots: [],
    wrong: 0, checks: 0, elapsed: 0, timer: null, done: false,
    soundOn: true, resetArm: false, resetTimer: null,
  };

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  /* —— 音效（WebAudio 本地合成，同栏口味）—— */
  /* 手机外放响度补偿（口径统一，由 scripts/boost-sfx-mobile.mjs 批量写入）：
     手机小喇叭对 500Hz 以下钝感、超短音感知响度低——触屏设备把提示音增益翻倍、
     低于 500Hz 的音移高八度、衰减拉长；桌面/耳机维持原响度。 */
  var SFX_MOBILE = false;
  try {
    SFX_MOBILE = (global.matchMedia && global.matchMedia('(pointer: coarse)').matches) ||
      (global.navigator && global.navigator.maxTouchPoints > 0);
  } catch (e) { /* 检测不可用按桌面处理 */ }
  var actx = null;
  function sfx(kind) {
    if (!S.soundOn) return;
    try {
      var AC = global.AudioContext || global.webkitAudioContext;
      if (!AC) return;
      if (!actx) actx = new AC();
      if (actx.state === 'suspended' && actx.resume) actx.resume();
      var t = actx.currentTime;
      var seq;
      if (kind === 'ok') seq = [[523, 0], [659, 0.07]];
      else if (kind === 'bad') seq = [[196, 0], [185, 0.09]];
      else seq = [[523, 0], [659, 0.1], [784, 0.2], [1046, 0.3]];   // win
      for (var i = 0; i < seq.length; i++) {
        var o = actx.createOscillator();
        var g = actx.createGain();
        o.type = 'triangle';
        o.frequency.setValueAtTime(SFX_MOBILE && seq[i][0] < 500 ? seq[i][0] * 2 : seq[i][0], t + seq[i][1]);
        g.gain.setValueAtTime(0.0001, t + seq[i][1]);
        g.gain.exponentialRampToValueAtTime(SFX_MOBILE ? 0.42 : 0.18, t + seq[i][1] + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, t + seq[i][1] + (SFX_MOBILE ? 0.2 : 0.14));
        o.connect(g);
        g.connect(actx.destination);
        o.start(t + seq[i][1]);
        o.stop(t + seq[i][1] + (SFX_MOBILE ? 0.26 : 0.16));
      }
    } catch (e) { /* 音频不可用静默 */ }
  }

  /* —— 偏好与成绩 —— */
  function save() {
    try { localStorage.setItem(PREF_KEY, JSON.stringify({ v: 1, level: S.level, soundOn: S.soundOn })); }
    catch (e) { /* 存储不可用静默 */ }
  }
  function restorePrefs() {
    try {
      var d = JSON.parse(localStorage.getItem(PREF_KEY) || '{}');
      if (d && d.v === 1) {
        if (LEVELS[d.level]) S.level = d.level;
        S.soundOn = d.soundOn !== false;
      }
    } catch (e) { /* 忽略脏数据 */ }
  }
  function loadRecord() {
    try {
      var r = JSON.parse(localStorage.getItem(RECORD_KEY) || '{}');
      return r && typeof r === 'object' && !Array.isArray(r) ? r : {};
    } catch (e) { return {}; }
  }

  /* ============ 渲染 ============ */

  function fmtTime(sec) {
    var m = Math.floor(sec / 60), s = sec % 60;
    return m + ' 分 ' + (s < 10 ? '0' : '') + s + ' 秒';
  }
  function fmtTimeShort(sec) {
    var m = Math.floor(sec / 60), s = sec % 60;
    return m ? m + ' 分 ' + (s < 10 ? '0' : '') + s + ' 秒' : s + ' 秒';
  }

  function renderLevels() {
    ui.levels.textContent = '';
    [1, 2, 3].forEach(function (lv) {
      var b = el('button', 'st-llk-pack-btn' + (S.level === lv ? ' is-active' : ''));
      b.type = 'button';
      b.setAttribute('data-level', String(lv));
      b.setAttribute('role', 'radio');
      b.setAttribute('aria-checked', S.level === lv ? 'true' : 'false');
      b.appendChild(el('span', 'st-llk-pack-name', LEVELS[lv].label));
      b.appendChild(el('small', 'st-llk-pack-hint', LEVELS[lv].hint));
      b.addEventListener('click', function () {
        if (S.level === lv) return;
        S.level = lv;
        renderLevels();
        newGame();
        save();
      });
      ui.levels.appendChild(b);
    });
  }

  function renderBoard() {
    ui.board.textContent = '';
    for (var i = 0; i < S.pool.length; i++) {
      (function (idx) {
        var num = S.pool[idx];
        var b = el('button', 'st-bj-card');
        b.type = 'button';
        b.textContent = num.text;
        b.setAttribute('data-val', String(num.value));
        b.setAttribute('aria-label', '数字 ' + num.text + '，如果它是剩下最小的就点它');
        b.addEventListener('click', function () { onPick(idx); });
        ui.board.appendChild(b);
      })(i);
    }
    renderSlots();
  }

  function renderSlots() {
    ui.slots.textContent = '';
    for (var i = 0; i < S.nums.length; i++) {
      var got = S.slots[i];
      var d = el('div', 'st-bj-slot' + (got ? ' is-filled' : ''));
      d.appendChild(el('span', 'st-bj-slot-no', '第 ' + (i + 1) + ' 小'));
      d.appendChild(el('strong', 'st-bj-slot-val', got ? got.text : '？'));
      ui.slots.appendChild(d);
    }
  }

  function renderStatus() {
    if (S.done) return;
    ui.status.textContent = '还剩 ' + (S.pool.length - S.slots.length) + ' 张没归位 · 点错 ' + S.wrong
      + ' 次 · 用时 ' + fmtTime(S.elapsed);
  }

  function renderNote(msg, tone) {
    ui.note.textContent = msg;
    ui.note.className = 'st-llk-note' + (tone ? ' is-' + tone : '');
  }

  function recordStat(label, value) {
    var d = el('div', 'st-llk-stat');
    d.appendChild(el('span', 'st-llk-stat-label', label));
    d.appendChild(el('strong', 'st-llk-stat-value', value));
    return d;
  }

  function renderRecord() {
    var box = ui.record;
    box.textContent = '';
    var rec = loadRecord();
    var any = [1, 2, 3].some(function (lv) { return rec[lv] && rec[lv].wins; });
    if (!any) {
      box.className = 'st-llk-record is-empty';
      box.appendChild(el('p', 'st-llk-record-empty', '这个档还没通关过——从小到大点完一局，这里记下你的最好成绩。'));
      return;
    }
    box.className = 'st-llk-record';
    [1, 2, 3].forEach(function (lv) {
      var r = rec[lv];
      box.appendChild(recordStat(LEVELS[lv].label.replace(' ', ''),
        r && r.wins ? (r.bestTime ? fmtTimeShort(r.bestTime) : '—') + ' · ' + r.wins + ' 次' : '—'));
    });
  }

  function renderButtons() {
    ui.resetBtn.classList.toggle('is-arm', S.resetArm);
    ui.resetBtn.textContent = S.resetArm ? '再点一次确认重置' : '重置成绩';
  }

  /* ============ 交互 ============ */

  function onPick(idx) {
    if (S.done) return;
    var num = S.pool[idx];
    // 剩余最小 = 未被选走的里 value 最小的
    var minVal = Infinity;
    for (var i = 0; i < S.pool.length; i++) {
      if (!S.picked[i] && S.pool[i].value < minVal) minVal = S.pool[i].value;
    }
    if (num.value !== minVal) {
      S.wrong++;
      sfx('bad');
      var card = ui.board.children[idx];
      if (card && card.classList) {
        card.classList.add('is-bad');
        setTimeout(function () { if (card.isConnected) card.classList.remove('is-bad'); }, 420);
      }
      renderNote('「' + num.text + '」不是现在最小的——盘里还有比它小的数。想想谁更小；拿不准就点「查一查」，我们把比较过程摆出来。', 'warn');
      renderStatus();
      return;
    }
    S.picked[idx] = true;
    S.slots.push(num);
    sfx('ok');
    var card2 = ui.board.children[idx];
    if (card2) card2.classList.add('is-gone');
    renderSlots();
    if (S.slots.length === S.nums.length) { renderStatus(); finish(); return; }
    renderNote('「' + num.text + '」就是现在最小的，放进第 ' + S.slots.length + ' 小。接着找。', 'ok');
    renderStatus();
  }

  /* 查一查：拿剩余最小和次小比给你看（讲真过程，不吊答案） */
  function checkHint() {
    if (S.done) return;
    S.checks++;
    var left = [];
    for (var i = 0; i < S.pool.length; i++) {
      if (!S.picked[i]) left.push(S.pool[i]);
    }
    left.sort(function (a, b) { return a.value - b.value; });
    if (!left.length) return;
    if (left.length === 1) {
      renderNote('只剩最后一张「' + left[0].text + '」了，点它收尾。（已查 ' + S.checks + ' 次）', '');
      return;
    }
    renderNote('现在最小的是「' + left[0].text + '」——它和「' + left[1].text + '」比：' + why(left[0], left[1])
      + '（已查 ' + S.checks + ' 次）', '');
    sfx('bad');
  }

  function startTimer() {
    stopTimer();
    S.elapsed = 0;
    S.timer = setInterval(function () {
      if (S.done) return;
      S.elapsed++;
      renderStatus();
    }, 1000);
  }
  function stopTimer() {
    if (S.timer) { clearInterval(S.timer); S.timer = null; }
  }

  function finish() {
    S.done = true;
    stopTimer();
    sfx('win');
    var rec = loadRecord();
    var cur = rec[S.level] || { plays: 0, wins: 0, bestTime: 0, bestWrong: null };
    cur.plays++;
    cur.wins++;
    var isBest = !cur.bestTime || S.elapsed < cur.bestTime;
    if (isBest) {
      cur.bestTime = S.elapsed;
      cur.bestWrong = S.wrong;
    }
    rec[S.level] = cur;
    try { localStorage.setItem(RECORD_KEY, JSON.stringify(rec)); } catch (e) { /* 静默 */ }
    ui.status.textContent = '全部比完！用时 ' + fmtTime(S.elapsed) + '，点错 ' + S.wrong + ' 次，查了 ' + S.checks + ' 次。'
      + (isBest ? '这是这个档的最好成绩。' : '（最好成绩 ' + fmtTime(cur.bestTime) + '）');
    ui.status.className = 'st-status is-over';
    renderNote('从小到大这一串就是全部答案——比较排序你亲手走了一遍：每一轮都挑出剩下最小的，'
      + '这正是计算机排序最朴素的思路。点「换一题」再来。', 'ok');
    renderRecord();
    renderButtons();
  }

  function newGame() {
    var spec = levelSpec(S.level);
    S.level = spec.level;
    var g = gen(spec.level);
    S.nums = g.nums.slice();
    // 乱序上盘（ Fisher–Yates ）；答案序 = 按 value 升序
    S.pool = g.nums.slice();
    for (var i = S.pool.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var t = S.pool[i]; S.pool[i] = S.pool[j]; S.pool[j] = t;
    }
    S.picked = {};
    S.slots = [];
    S.wrong = 0;
    S.checks = 0;
    S.done = false;
    S.resetArm = false;
    if (S.resetTimer) { clearTimeout(S.resetTimer); S.resetTimer = null; }
    ui.status.className = 'st-status';
    renderStatus();
    renderNote('把 ' + g.n.length + ' 个数从小到大点出来：每轮点「剩下最小的那个」，点对就进槽。'
      + '点错会提醒你；拿不准点「查一查」，比较过程摆给你看。', '');
    renderBoard();
    renderRecord();
    renderButtons();
    startTimer();
  }

  function resetRecord() {
    if (!S.resetArm) {
      S.resetArm = true;
      renderButtons();
      if (S.resetTimer) clearTimeout(S.resetTimer);
      S.resetTimer = setTimeout(function () { S.resetArm = false; S.resetTimer = null; renderButtons(); }, 3000);
      return;
    }
    S.resetArm = false;
    if (S.resetTimer) { clearTimeout(S.resetTimer); S.resetTimer = null; }
    try { localStorage.setItem(RECORD_KEY, JSON.stringify({})); } catch (e) { /* 静默 */ }
    renderRecord();
    renderButtons();
    renderNote('已重置比大小的本机成绩。', '');
  }

  /* 音效选择器（口径同汉诺塔） */
  function bindSoundPicker() {
    var btns = root.querySelectorAll('[data-st-sound]');
    for (var i = 0; i < btns.length; i++) {
      (function (b) {
        b.addEventListener('click', function () {
          S.soundOn = b.getAttribute('data-st-sound') === 'on';
          syncSoundPicker();
          save();
          if (S.soundOn) sfx('ok');
        });
      })(btns[i]);
    }
    syncSoundPicker();
  }
  function syncSoundPicker() {
    var btns = root.querySelectorAll('[data-st-sound]');
    for (var i = 0; i < btns.length; i++) {
      var on = (btns[i].getAttribute('data-st-sound') === 'on') === S.soundOn;
      btns[i].classList.toggle('is-active', on);
      btns[i].setAttribute('aria-checked', on ? 'true' : 'false');
    }
  }

  /* ============ boot ============ */

  function boot() {
    root = document.querySelector('[data-st-game]');
    if (!root) return;
    ui.board = root.querySelector('[data-st-bj-board]');
    ui.slots = root.querySelector('[data-st-bj-slots]');
    ui.status = root.querySelector('[data-st-status]');
    ui.note = root.querySelector('[data-st-note]');
    ui.levels = root.querySelector('[data-st-bj-levels]');
    ui.record = root.querySelector('[data-st-record]');
    ui.newBtn = root.querySelector('[data-st-new]');
    ui.checkBtn = root.querySelector('[data-st-check]');
    ui.resetBtn = root.querySelector('[data-st-reset]');
    if (!ui.board) return;

    restorePrefs();
    renderLevels();
    bindSoundPicker();

    ui.newBtn.addEventListener('click', function () { newGame(); });
    ui.checkBtn.addEventListener('click', checkHint);
    ui.resetBtn.addEventListener('click', resetRecord);

    newGame();
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
  }

  /* ============ 冒烟与单测钩子 ============ */

  global.STBijiao = {
    levelSpec: levelSpec,
    gen: gen,
    cmp: cmp,
    why: why,
    fmt: fmt,
    gcd: gcd,
    lcm: lcm,
  };
})(typeof window !== 'undefined' ? window : this);
